import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { 
  FiPlay, FiCheck, FiStar, FiUsers, FiTrendingUp, FiTarget, 
  FiClock, FiGift, FiShield, FiPhone, FiMail, FiChevronDown,
  FiArrowRight, FiZap, FiDollarSign, FiBookOpen, FiAward, FiX, FiCalendar,
  FiBarChart3, FiCrown, FiTrophy, FiDiamond, FiLock, FiUnlock, FiMic,
  FiVideo, FiHeadphones, FiMonitor, FiBriefcase, FiGraduationCap, FiSettings
} = FiIcons;

// Registration Modal Component
const RegistrationModal = ({ isOpen, onClose, formData, setFormData, onSubmit }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 border-2 border-blue-400/50 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
          >
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-2">
                Secure Your Elite Seat
              </h2>
              <p className="text-gray-400">Enter your details to proceed to payment.</p>
            </div>
            
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-bold text-blue-300 mb-2">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  id="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border-2 border-blue-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-blue-300 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border-2 border-blue-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-bold text-blue-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border-2 border-blue-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="Enter your phone number"
                />
              </div>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-700 text-white font-black py-4 px-6 rounded-xl text-lg shadow-2xl flex items-center justify-center gap-2 border-2 border-blue-400"
              >
                <SafeIcon icon={FiLock} className="w-5 h-5" />
                Proceed to Payment
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


function App() {
  // Evergreen timer that resets every 15 minutes
  const [bonusTimeLeft, setBonusTimeLeft] = useState({
    minutes: 14,
    seconds: 59
  });
  const [seatsLeft] = useState(42);
  const [openFaq, setOpenFaq] = useState(null);

  // State for modal and form data
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: ''
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setBonusTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { minutes: prev.minutes - 1, seconds: 59 };
        } else {
          // Reset to 15 minutes when timer reaches zero
          return { minutes: 14, seconds: 59 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Corporate Trainer turned AI Coach",
      result: "₹25+ Lakhs Revenue",
      period: "6 Months",
      quote: "Corporate training-ல இருந்து AI coaching business start பண்ணி monthly ₹4+ lakhs earn பண்ணறேன். Clients worldwide!",
      rating: 5,
      multiplier: "10X Growth",
      age: "35, Leadership Coach"
    },
    {
      name: "Priya Natarajan", 
      role: "Life Coach & Digital Entrepreneur",
      result: "₹8.5 Lakhs",
      period: "120 Days",
      quote: "Life coaching-ஐ AI-ன் மூலம் scale பண்ணி international clients-ஐ reach பண்ணினேன். Game changer!",
      rating: 5,
      multiplier: "15X ROI",
      age: "31, Wellness Coach"
    },
    {
      name: "Suresh Babu",
      role: "Sales Trainer & Business Coach",
      result: "₹12,999 x 20 Sales",
      period: "Single Month", 
      quote: "AI-powered sales training programs create பண்ணி ₹2.6 lakhs in one month! My expertise is now scalable!",
      rating: 5,
      multiplier: "Instant Scale",
      age: "38, Sales Expert"
    }
  ];

  const bonuses = [
    {
      title: "Elite Coaches Mastermind",
      value: "₹35,000",
      description: "Exclusive community of successful Tamil coaches with weekly strategy sessions",
      icon: FiCrown,
      premium: true
    },
    {
      title: "AI Coaching Templates Library",
      value: "₹25,000", 
      description: "Ready-made AI systems for coaching, training & consulting businesses",
      icon: FiBookOpen,
      premium: true
    },
    {
      title: "Personal AI Training Assistant",
      value: "₹15,000",
      description: "Custom AI that handles client queries and delivers your training content 24/7",
      icon: FiMic,
      premium: false
    },
    {
      title: "Coaches Revenue Calculator",
      value: "₹12,000",
      description: "Advanced AI tool to maximize your coaching income potential",
      icon: FiBarChart3,
      premium: false
    }
  ];

  const faqs = [
    {
      question: "Workshop coaches & trainers-க்கு specially design பண்ணியிருக்கா?",
      answer: "Absolutely! இது 100% coaching & training professionals-க்கு specially designed workshop. Client acquisition, content scaling, premium positioning எல்லாத்தையும் focus பண்ணி curriculum design பண்ணியிருக்கோம்."
    },
    {
      question: "Existing coaching business-ஐ AI-ன் மூலம் scale பண்ணலாமா?",
      answer: "Yes! Most successful students existing coaches & trainers தான். உங்க expertise-ஐ AI-ன் மூலம் automate பண்ணி, more clients reach பண்ணி, premium pricing charge பண்ணலாம்."
    },
    {
      question: "Technical background இல்லாம AI coaching tools use பண்ணலாமா?",
      answer: "Definitely! Zero technical knowledge இருந்தாலும் problem இல்லை. Coach-friendly Tamil explanations, step-by-step guidance, 24/7 support team இருக்கு. Simple tools மட்டும் தான் use பண்ணுவோம்."
    },
    {
      question: "International clients-ஐ reach பண்ண முடியுமா?",
      answer: "Absolutely! AI tools-ன் மூலம் language barriers break பண்ணி global market-ல compete பண்ணலாம். Many students worldwide clients serve பண்ணறாங்க premium rates-ல."
    },
    {
      question: "Coaching income எவ்வளவு increase ஆகும்?",
      answer: "Average-ஆ 5-10X income increase expect பண்ணலாம். First month setup, second month client acquisition, third month premium positioning achieve பண்ணலாம். 95% coaches successful results achieve பண்ணியிருக்காங்க!"
    }
  ];

  // Opens the registration modal
  const openRegistrationModal = () => {
    setIsModalOpen(true);
  };

  // Handles form submission and redirects to payment page
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const baseUrl = "https://paymentz.aifunnels.app/product/21362/page/18489/addon-page";
    
    // Construct query parameters with CORRECTED keys
    const params = new URLSearchParams({
      full_name: formData.fullName,   // CHANGED from 'name'
      email: formData.email,
      phone_no: formData.phoneNumber, // CHANGED from 'phone'
      staffId: '64304', 
      ac: '1'          
    });

    const paymentUrl = `${baseUrl}?${params.toString()}`;
    
    window.open(paymentUrl, '_blank');
    
    setIsModalOpen(false);
  };


  // Floating elements for premium feel
  const FloatingElement = ({ children, delay = 0 }) => (
    <motion.div
      animate={{
        y: [-10, 10, -10],
        rotate: [-1, 1, -1],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );

  // Mobile-Optimized Sticky Button
  const StickyButton = () => (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 bg-gradient-to-t from-black via-black/95 to-transparent backdrop-blur-sm border-t border-blue-400/20">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={openRegistrationModal}
        className="w-full bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-700 text-white font-black py-4 px-6 rounded-2xl text-base shadow-2xl flex items-center justify-center gap-2 border-2 border-blue-400 mobile-btn"
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 25%, #1d4ed8 50%, #3b82f6 75%, #4f46e5 100%)',
          backgroundSize: '300% 300%',
          animation: 'gradient-shift 3s ease infinite'
        }}
      >
        <SafeIcon icon={FiMic} className="w-5 h-5 flex-shrink-0" />
        <span className="font-black text-center leading-tight">
          Secure My Coach Seat
          <br />
          <span className="text-sm font-bold">Only {seatsLeft} Left!</span>
        </span>
        <SafeIcon icon={FiArrowRight} className="w-5 h-5 flex-shrink-0" />
      </motion.button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Render the modal */}
      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleFormSubmit}
      />
      
      {/* Premium Background Pattern - Mobile Optimized */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-400 to-indigo-600"></div>
        <div className="absolute top-10 left-10 w-48 h-48 sm:w-96 sm:h-96 bg-blue-400 rounded-full blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 sm:w-80 sm:h-80 bg-indigo-500 rounded-full blur-3xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-gradient-radial from-blue-300 to-transparent opacity-5 animate-spin-slow"></div>
      </div>

      {/* Mobile Sticky Button */}
      <StickyButton />

      {/* Main Content with Bottom Padding for Sticky Button */}
      <div className="pb-24">
        {/* Hero Section - Mobile Optimized */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
          {/* Premium Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-blue-500/10"></div>
          
          {/* Mobile Floating Elements */}
          <FloatingElement delay={0}>
            <div className="absolute top-16 left-6 w-3 h-3 sm:w-4 sm:h-4 bg-blue-400 rounded-full blur-sm opacity-60"></div>
          </FloatingElement>
          <FloatingElement delay={2}>
            <div className="absolute top-32 right-8 w-4 h-4 sm:w-6 sm:h-6 bg-indigo-500 rounded-full blur-sm opacity-40"></div>
          </FloatingElement>
          <FloatingElement delay={4}>
            <div className="absolute bottom-32 left-8 w-2 h-2 sm:w-3 sm:h-3 bg-blue-300 rounded-full blur-sm opacity-50"></div>
          </FloatingElement>

          <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-4 sm:space-y-6"
            >
              {/* Mobile Premium Trust Badges */}
              <div className="flex flex-wrap justify-center gap-2 mb-4 sm:mb-6">
                <div className="bg-gradient-to-r from-blue-400/20 to-indigo-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-3 py-1 text-blue-300 text-xs font-bold">
                  <SafeIcon icon={FiMic} className="inline mr-1 w-3 h-3" />
                  2000+ Coaches Success
                </div>
                <div className="bg-gradient-to-r from-blue-400/20 to-indigo-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-3 py-1 text-blue-300 text-xs font-bold">
                  <SafeIcon icon={FiDollarSign} className="inline mr-1 w-3 h-3" />
                  ₹8+ Crore Generated
                </div>
                <div className="bg-gradient-to-r from-blue-400/20 to-indigo-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-3 py-1 text-blue-300 text-xs font-bold">
                  <SafeIcon icon={FiCrown} className="inline mr-1 w-3 h-3" />
                  Elite Training
                </div>
              </div>

              {/* Mobile Premium Headline */}
              <div className="space-y-2 sm:space-y-4">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-blue-500 leading-tight px-2"
                  style={{
                    textShadow: '0 0 30px rgba(59, 130, 246, 0.5)',
                  }}
                >
                  AI-யால் உங்கள் Coaching-ஐ
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-3xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white mb-2 sm:mb-4"
                  style={{
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                  }}
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Scale</span> பண்ணுங்கள்
                </motion.div>
              </div>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="text-lg sm:text-2xl md:text-3xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-2 font-medium"
              >
                <span className="text-blue-400 font-bold">Coaches-only Workshop:</span> AI-ன் மூலம் 
                <span className="text-indigo-400 font-bold"> Premium Coaching Business</span> build பண்ணுங்கள்!
              </motion.p>

              {/* Mobile Premium Workshop Schedule */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
                className="bg-gradient-to-r from-blue-500/10 to-indigo-600/10 backdrop-blur-lg border border-blue-400/30 rounded-2xl p-4 sm:p-6 max-w-sm mx-auto shadow-2xl"
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiCalendar} className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-blue-400 font-bold text-base">Elite Coaches Workshop</p>
                </div>
                <div className="text-white text-center space-y-1">
                  <div className="text-xl font-black text-blue-400">This Sunday</div>
                  <div className="text-base text-gray-300">10:00 AM - 1:00 PM IST</div>
                  <div className="text-xs text-indigo-400 font-medium bg-indigo-400/10 rounded-full px-3 py-1 inline-block">
                    Limited to 50 Elite Coaches
                  </div>
                </div>
              </motion.div>

              {/* Mobile Premium Countdown Timer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="bg-gradient-to-r from-red-600/20 to-orange-600/20 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-4 sm:p-6 max-w-xs mx-auto shadow-2xl"
              >
                <div className="flex items-center justify-center gap-1 mb-3">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-red-400" />
                  <p className="text-red-400 font-bold text-sm text-center">🚨 Elite Bonuses Expire In:</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-3 border border-red-400">
                    <div className="text-2xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-xs text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-3 border border-red-400">
                    <div className="text-2xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-xs text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
                <div className="text-xs text-red-300 mt-3 font-medium text-center">Worth ₹87,000+ Elite Bonuses!</div>
              </motion.div>

              {/* Mobile Premium CTA */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                className="space-y-4 px-2"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openRegistrationModal}
                  className="bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-700 text-white font-black py-5 px-8 rounded-2xl text-lg shadow-2xl border-4 border-blue-400 w-full relative overflow-hidden mobile-btn"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <SafeIcon icon={FiMic} className="w-5 h-5" />
                    My Elite Coach Seat-ஐ Reserve பண்ணுங்கள்
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                </motion.button>
                
                <div className="flex flex-col items-center gap-2">
                  <div className="text-red-400 font-bold text-lg text-center">
                    ⚠️ Only {seatsLeft} Elite Seats Remaining!
                  </div>
                  <div className="text-blue-400 text-sm font-medium text-center">
                    Join 2000+ Successful Tamil Coaches & Trainers
                  </div>
                </div>
              </motion.div>

              {/* Mobile Premium Guarantee */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.1 }}
                className="flex justify-center px-2"
              >
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/50 rounded-full px-4 py-2 text-green-300 text-sm font-bold backdrop-blur-sm text-center">
                  <SafeIcon icon={FiShield} className="inline mr-1 w-4 h-4" />
                  100% Coach Success Guarantee
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Problem & Solution Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-blue-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
              {/* Problems - Mobile Optimized */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500 mb-6 text-center lg:text-left">
                  உங்கள் Coaching Business-ல இந்த Challenges இருக்கா?
                </h2>
                
                <div className="space-y-4">
                  {[
                    { problem: "Time for money exchange - scale பண்ண முடியலை", icon: FiClock },
                    { problem: "Premium pricing charge பண்ண confidence இல்லை", icon: FiDollarSign },
                    { problem: "International market-ல compete பண்ண tough", icon: FiUsers },
                    { problem: "Content creation & delivery-ல time consuming", icon: FiBookOpen },
                    { problem: "Client acquisition cost அதிகம் - ROI குறைவு", icon: FiTarget }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-400/30 rounded-xl backdrop-blur-sm hover:border-red-400/50 transition-all"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={item.icon} className="text-white w-5 h-5" />
                      </div>
                      <p className="text-gray-300 text-base font-medium leading-snug">{item.problem}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Solutions - Mobile Optimized */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-6 text-center lg:text-left">
                  AI-Powered Coaching Solutions!
                </h2>
                
                <div className="space-y-4">
                  {[
                    { solution: "AI automation-ன் மூலம் scalable coaching systems", icon: FiZap, multiplier: "Scale 10X" },
                    { solution: "Premium positioning & international market access", icon: FiCrown, multiplier: "Premium" },
                    { solution: "24/7 AI assistant for client support & delivery", icon: FiMic, multiplier: "Automated" },
                    { solution: "Content multiplication & personalized delivery", icon: FiVideo, multiplier: "Efficient" },
                    { solution: "Data-driven client acquisition & retention", icon: FiBarChart3, multiplier: "Smart ROI" }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-400/30 rounded-xl backdrop-blur-sm hover:border-blue-400/50 transition-all group"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={item.icon} className="text-white w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-300 text-base font-medium mb-2 leading-snug">{item.solution}</p>
                        <span className="inline-block bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {item.multiplier}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mobile Premium Social Proof Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-blue-900 to-black relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/3 to-indigo-500/3"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-6">
                Real Success Stories from Tamil Coaches
              </h2>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-blue-400">2000+</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Coaches</div>
                </div>
                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-blue-400">₹8+ Cr</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Generated</div>
                </div>
                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-blue-400">98%</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Success</div>
                </div>
              </div>
            </motion.div>

            <div className="space-y-6 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-b from-gray-800/50 to-blue-900/50 backdrop-blur-lg border border-blue-400/20 rounded-2xl p-6 shadow-2xl hover:border-blue-400/40 transition-all"
                >
                  {/* Rating Stars */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-blue-400 fill-current" />
                    ))}
                  </div>
                  
                  {/* Results */}
                  <div className="mb-4">
                    <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">
                      {testimonial.result}
                    </div>
                    <div className="text-gray-400 text-sm mb-2">in {testimonial.period}</div>
                    <div className="inline-block bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full mb-2">
                      {testimonial.multiplier}
                    </div>
                    <div className="text-blue-300 text-xs font-medium">{testimonial.age}</div>
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-gray-300 mb-4 italic text-sm leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="border-t border-gray-700 pt-4">
                    <div className="font-bold text-white text-base">{testimonial.name}</div>
                    <div className="text-blue-400 text-sm font-medium">{testimonial.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium Curriculum Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-blue-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-4">
                3 Hours = Elite Coach Transformation
              </h2>
              <p className="text-lg text-gray-300 font-medium">Complete AI-Powered Coaching Business Blueprint</p>
            </motion.div>

            <div className="space-y-6 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0">
              {[
                {
                  hour: "Hour 1",
                  title: "AI Foundation for Coaches",
                  subtitle: "From Traditional to AI-Powered Coaching",
                  icon: FiZap,
                  color: "from-purple-500 to-blue-600",
                  items: [
                    "Coach-specific AI tools introduction in Tamil",
                    "Client assessment & personalization automation", 
                    "Content creation & delivery systems",
                    "Building authority with AI assistance"
                  ]
                },
                {
                  hour: "Hour 2", 
                  title: "Scale Your Coaching Impact",
                  subtitle: "Multiply Your Reach & Revenue",
                  icon: FiMic,
                  color: "from-blue-500 to-indigo-600",
                  items: [
                    "AI-powered client acquisition strategies",
                    "Premium positioning & pricing psychology",
                    "International market expansion tactics",
                    "Automated follow-up & retention systems"
                  ]
                },
                {
                  hour: "Hour 3",
                  title: "Elite Coach Business Model",
                  subtitle: "Build Your Coaching Empire",
                  icon: FiCrown,
                  color: "from-indigo-500 to-blue-600",
                  items: [
                    "Advanced AI coaching methodologies",
                    "Team building & delegation strategies",
                    "Multiple revenue streams creation",
                    "Long-term wealth & legacy building"
                  ]
                }
              ].map((module, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-gradient-to-b from-gray-800/50 to-blue-900/50 backdrop-blur-lg border border-blue-400/20 rounded-2xl p-6 shadow-2xl hover:border-blue-400/40 transition-all"
                >
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${module.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <SafeIcon icon={module.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-sm font-bold text-blue-400 mb-2">{module.hour}</div>
                    <h3 className="text-xl font-black text-white mb-2">{module.title}</h3>
                    <p className="text-indigo-400 font-bold text-base">{module.subtitle}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    {module.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <SafeIcon icon={FiCheck} className="text-white w-3 h-3" />
                        </div>
                        <span className="text-gray-300 font-medium text-sm leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Mobile Premium Learning Outcomes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-gradient-to-r from-blue-500/20 to-indigo-600/20 backdrop-blur-lg border-2 border-blue-400/30 rounded-2xl p-6 text-center"
            >
              <h3 className="text-2xl sm:text-3xl font-black text-blue-400 mb-6">Workshop முடிஞ்சதும் உங்ககிட்ட இருக்கும்:</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { outcome: "Complete AI coaching system", icon: FiZap },
                  { outcome: "Premium positioning strategy", icon: FiCrown },
                  { outcome: "Scalable revenue model", icon: FiDollarSign },
                  { outcome: "Global market access", icon: FiTrendingUp }
                ].map((item, index) => (
                  <div key={index} className="flex flex-col items-center gap-2 bg-gradient-to-b from-blue-400/10 to-indigo-500/10 border border-blue-400/30 rounded-xl p-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={item.icon} className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-bold text-center text-sm leading-tight">{item.outcome}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Bonus Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-blue-900 to-black relative overflow-hidden">
          {/* Premium Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/10 to-indigo-600/10"></div>
            <FloatingElement delay={0}>
              <div className="absolute top-20 left-20 w-24 h-24 sm:w-32 sm:h-32 bg-blue-400/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={3}>
              <div className="absolute bottom-20 right-20 w-32 h-32 sm:w-40 sm:h-40 bg-indigo-500/20 rounded-full blur-2xl"></div>
            </FloatingElement>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-6">
                ₹87,000+ Elite Coach Bonuses - FREE!
              </h2>
              <div className="text-4xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 mb-8">
                Total Value: ₹1,50,000+
              </div>
              
              {/* Mobile Premium Bonus Timer */}
              <div className="bg-gradient-to-r from-red-600/30 to-orange-600/30 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-6 max-w-sm mx-auto shadow-2xl">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-red-400" />
                  <p className="text-red-400 font-black text-lg">⏰ Elite Bonuses Expire In:</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bonuses.map((bonus, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-b from-gray-800/60 to-blue-900/60 backdrop-blur-lg border-2 ${
                    bonus.premium ? 'border-blue-400/50 shadow-blue-400/20' : 'border-gray-600/50'
                  } rounded-2xl p-6 shadow-2xl hover:border-blue-400/70 transition-all group relative overflow-hidden`}
                >
                  {/* Premium Badge */}
                  {bonus.premium && (
                    <div className="absolute top-3 right-3">
                      <div className="bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-xs font-black px-2 py-1 rounded-full flex items-center gap-1">
                        <SafeIcon icon={FiMic} className="w-3 h-3" />
                        ELITE VIP
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <div className={`w-16 h-16 ${
                      bonus.premium 
                        ? 'bg-gradient-to-r from-blue-400 to-indigo-500 shadow-blue-400/50' 
                        : 'bg-gradient-to-r from-gray-600 to-gray-700'
                    } rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl`}>
                      <SafeIcon icon={bonus.icon} className={`w-8 h-8 ${bonus.premium ? 'text-white' : 'text-white'}`} />
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-black py-1 px-3 rounded-full inline-block mb-3">
                      FREE BONUS
                    </div>
                    
                    <h3 className="text-lg font-black text-white mb-3">{bonus.title}</h3>
                    <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 mb-3">
                      {bonus.value}
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{bonus.description}</p>
                  </div>

                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium About Instructor */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-blue-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <div className="space-y-8 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start lg:space-y-0">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative group">
                  {/* Mobile Premium Image Container */}
                  <div className="w-full h-72 sm:h-80 lg:h-[420px] rounded-2xl overflow-hidden shadow-2xl border-4 border-blue-400/30 group-hover:border-blue-400/50 transition-all">
                    <img 
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759040594802-Abdul%20Hakkim.webp" 
                      alt="Abdul Hakkim - Elite Coach Mentor"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center" style={{display: 'none'}}>
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl">
                          <span className="text-3xl font-black text-white">AH</span>
                        </div>
                        <div className="text-xl font-black text-gray-900">Abdul Hakkim</div>
                        <div className="text-gray-700 font-medium">Elite Coach Mentor</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile Premium Floating Badges */}
                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full p-3 shadow-2xl">
                    <SafeIcon icon={FiMic} className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-3 shadow-2xl">
                    <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-white" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-4">
                    Meet Your Elite Coach Mentor
                  </h2>
                  <div className="text-xl font-bold text-white">Abdul Hakkim</div>
                  <div className="text-lg text-blue-400 font-medium">Elite AI Coaching Transformation Expert</div>
                </div>
                
                {/* Mobile Premium Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { metric: "₹8+ Crore", label: "Generated by Coaches", icon: FiDollarSign },
                    { metric: "2000+", label: "Coaches Transformed", icon: FiMic },
                    { metric: "4 Years", label: "Elite Training", icon: FiCrown }
                  ].map((stat, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-xl p-4 text-center backdrop-blur-sm hover:border-blue-400/50 transition-all"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <SafeIcon icon={stat.icon} className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-xl font-black text-blue-400">{stat.metric}</div>
                      <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile Premium Story */}
                <div className="space-y-4 text-gray-300 text-base leading-relaxed">
                  <p className="bg-gradient-to-r from-gray-800/50 to-blue-900/50 border border-blue-400/20 rounded-xl p-4 backdrop-blur-sm">
                    <span className="text-blue-400 font-bold">"Elite coaching transformation"</span> என் specialty. 
                    2000+ Tamil coaches & trainers-ஐ AI-ன் மூலம் premium level-க்கு elevate பண்ணியிருக்கேன். 
                  </p>
                  <p className="bg-gradient-to-r from-gray-800/50 to-blue-900/50 border border-blue-400/20 rounded-xl p-4 backdrop-blur-sm">
                    <span className="text-green-400 font-bold">"Special expertise"</span> life coaches, corporate trainers, business consultants-க்கு. 
                    <span className="text-blue-400 font-bold"> AI-powered scaling methodologies</span> design பண்ணி teach பண்ணுவேன்.
                  </p>
                  <p className="text-center text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-xl p-4">
                    "உங்க coaching expertise-ஐ global level-க்கு scale பண்ண ready!"
                  </p>
                </div>

                {/* Mobile Premium Credentials */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-full px-4 py-2 justify-center">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
                    <span className="text-green-300 font-bold text-sm">2000+ Coach Success Stories</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/30 rounded-full px-4 py-2 justify-center">
                    <SafeIcon icon={FiMic} className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300 font-bold text-sm">Elite Coaching Leader</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mobile Premium FAQ Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-blue-900 to-black relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/3 to-indigo-500/3"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-4">
                Coaches Common Questions
              </h2>
              <p className="text-lg text-gray-400">எல்லா coaching doubts-ம் clear பண்ணிக்கோங்கள்</p>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-r from-gray-800/50 to-blue-900/50 backdrop-blur-lg border border-blue-400/20 rounded-xl shadow-xl hover:border-blue-400/40 transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-4 sm:px-6 py-4 text-left flex justify-between items-center hover:bg-blue-400/5 rounded-xl transition-all"
                  >
                    <span className="font-bold text-white text-base pr-4 leading-snug">{faq.question}</span>
                    <div className="flex items-center gap-2">
                      <SafeIcon 
                        icon={FiChevronDown} 
                        className={`w-5 h-5 text-blue-400 transition-transform flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 sm:px-6 pb-4 text-gray-300 text-base leading-relaxed border-t border-blue-400/20 pt-4">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium Final CTA Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-br from-blue-900 via-black to-indigo-900 relative overflow-hidden">
          {/* Premium Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10"></div>
            <FloatingElement delay={0}>
              <div className="absolute top-10 left-10 w-24 h-24 sm:w-32 sm:h-32 bg-blue-400/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={2}>
              <div className="absolute bottom-10 right-10 w-32 h-32 sm:w-40 sm:h-40 bg-indigo-500/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={4}>
              <div className="absolute top-1/2 left-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-blue-300/15 rounded-full blur-xl"></div>
            </FloatingElement>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 leading-tight">
                Ready to Become an Elite AI Coach?
              </h2>

              <p className="text-xl text-gray-300 font-medium max-w-2xl mx-auto">
                <span className="text-blue-400 font-bold">3 மணி நேரத்தில்</span> உங்கள் coaching business-ஐ transform பண்ணுங்கள்!
              </p>

              {/* Mobile Final Premium Timer */}
              <div className="bg-gradient-to-r from-red-600/30 to-orange-600/30 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-6 max-w-xs mx-auto shadow-2xl">
                <p className="text-red-400 font-black text-lg mb-4">⏰ Last Chance - Elite Bonuses Expire:</p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 px-2">
                <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-400/50 rounded-xl p-4 max-w-sm mx-auto">
                  <div className="text-red-400 font-black text-xl mb-2">
                    🚨 FINAL WARNING
                  </div>
                  <div className="text-white font-bold text-lg">
                    Only {seatsLeft} Elite Coach Seats Left!
                  </div>
                  <div className="text-gray-300 text-sm mt-2">
                    Workshop starts in few hours. Transform your coaching today!
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openRegistrationModal}
                  className="bg-gradient-to-r from-blue-500 via-indigo-600 to-blue-700 text-white font-black py-6 px-12 rounded-2xl text-xl shadow-2xl border-4 border-blue-400 w-full relative overflow-hidden mobile-btn"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 25%, #1d4ed8 50%, #3b82f6 75%, #4f46e5 100%)',
                    backgroundSize: '300% 300%',
                    animation: 'gradient-shift 3s ease infinite'
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <SafeIcon icon={FiMic} className="w-6 h-6" />
                    YES! Transform My Coaching with AI
                    <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                </motion.button>

                {/* Mobile Premium Guarantees */}
                <div className="flex flex-col gap-3">
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/50 rounded-full px-4 py-2 text-green-300 font-bold backdrop-blur-sm text-center">
                    <SafeIcon icon={FiShield} className="inline mr-2 w-4 h-4" />
                    100% Elite Coach Success Guarantee
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-2 border-purple-400/50 rounded-full px-3 py-2 text-purple-300 font-bold backdrop-blur-sm text-center text-sm">
                      <SafeIcon icon={FiLock} className="inline mr-1 w-4 h-4" />
                      Secure Payment
                    </div>
                    <div className="flex-1 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-2 border-blue-400/50 rounded-full px-3 py-2 text-blue-300 font-bold backdrop-blur-sm text-center text-sm">
                      <SafeIcon icon={FiMic} className="inline mr-1 w-4 h-4" />
                      Elite Training
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
                Join 2000+ Tamil coaches who scaled their impact with AI. 
                <span className="text-blue-400 font-bold"> Your elite transformation starts today!</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Footer */}
        <footer className="bg-black border-t border-blue-400/20 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="space-y-6 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 mb-3">
                  Elite AI Coach Academy
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Tamil coaches & trainers-க்கு AI-ன் மூலம் premium coaching business build பண்ண உதவும் elite academy.
                </p>
              </div>
              
              <div className="text-center sm:text-left">
                <h4 className="text-lg font-bold text-white mb-4">Elite Support</h4>
                <div className="space-y-3 text-gray-400">
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-sm">+91 9884899080</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiMail} className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-sm">support@yinguru.com</span>
                  </div>
                </div>
              </div>

              <div className="text-center sm:text-left lg:col-span-1 sm:col-span-2 lg:col-span-1">
                <h4 className="text-lg font-bold text-white mb-4">Legal</h4>
                <div className="space-y-2 text-gray-400 text-sm">
                  <div><a href="https://funnelsdone.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors font-medium">Terms & Conditions</a></div>
                  <div><a href="https://funnelsdone.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors font-medium">Privacy Policy</a></div>
                  <div><a href="https://funnelsdone.com/refund-policy" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors font-medium">Refund Policy</a></div>
                </div>
              </div>
            </div>
            
            {/* Mobile Premium Disclaimer */}
            <div className="border-t border-gray-800 mt-6 pt-6">
              <div className="text-xs text-gray-500 leading-relaxed mb-4 bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                <strong className="text-blue-400">Disclaimer:</strong> This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, This site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. Results shown are from real coach students but individual results may vary based on effort and implementation. We provide proven strategies and tools designed specifically for coaches and trainers, but your success depends on your commitment to applying what you learn.
              </div>
              <div className="text-center text-gray-400">
                <p className="font-medium text-sm">&copy; 2024 Elite AI Coach Academy. All rights reserved.</p>
                <p className="text-xs mt-2 text-blue-400">Transforming Tamil Coaches through AI Innovation</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;